import React, { useEffect, useState } from "react";
import { Box, Button, Divider, Stack, Typography, useTheme } from "@mui/material";
import { getAssetHierarchyPalette, getContainedButtonSx, getOutlinedButtonSx } from "../../cmms/assetHierarchyStyles";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { stocktakeSparePart } from "../../../fetchers/cmmsSparePart";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import { formatQuantity } from "../../cmms/cmmsFormat";
import cmmsMessages from "../../cmms/cmmsMessages";
import messages from "../messages";
import { computeStocktakeDelta, getStockMovementQuantityColor } from "../stockMovements";
import FieldError from "./FieldError";

/*
 * File a physical count against one part.
 *
 * The field is what is on the shelf, never a difference. A client that works out its own delta
 * works it out against a balance it read a while ago and then writes that stale arithmetic into
 * the ledger; the cmms/poc branch took a delta for exactly this reason and had no way to notice.
 *
 * The difference is still shown, because a person typing 5 into a book that says 7 should see
 * what they are about to assert before they assert it. It is a preview computed with the same
 * subtraction the service uses — not what gets sent.
 */
export default function StocktakeDialog({ open, onClose, sparePart, companyKey }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({ countedQuantity: "", reason: "" });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({ countedQuantity: "", reason: "" });
    setErrors({});
  }, [open]);

  const handleTextChange = (event) => {
    const { name, id, value } = event.target;
    setForm((current) => ({ ...current, [name || id]: value }));
  };

  const balance = Number(sparePart?.quantityOnHand ?? 0);
  const hasCount = form.countedQuantity !== "" && !Number.isNaN(Number(form.countedQuantity));
  const delta = hasCount ? computeStocktakeDelta(form.countedQuantity, balance) : null;

  const stocktakeMutation = useMutation({
    mutationFn: (payload) => stocktakeSparePart(sparePart.id, payload),
  });

  const validate = () => {
    const next = {};
    const counted = Number(form.countedQuantity);

    if (form.countedQuantity === "" || Number.isNaN(counted)) {
      next.countedQuantity = intl.formatMessage(messages.validationNumber);
    } else if (counted < 0) {
      // Narrower than the service, which accepts it. A shelf cannot hold minus two, and the
      // only way to type it here is by mistake. Noted in the plan as a client-only rule.
      next.countedQuantity = intl.formatMessage(messages.validationCountedNotNegative);
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const result = await stocktakeMutation.mutateAsync({
        companyKey,
        countedQuantity: Number(form.countedQuantity),
        reason: form.reason?.trim() || null,
        // No countedByUserId — who counted comes from the token.
      });

      await queryClient.invalidateQueries({ queryKey: ["cmmsSpareParts"] });
      await queryClient.invalidateQueries({ queryKey: ["cmmsStockMovements", companyKey, sparePart.id] });

      // The server's answer, not the number that was typed — and those are genuinely two
      // different things. StocktakeAsync used to return countedQuantity, so this
      // comment described an intent the code could not carry out. It now re-derives the balance
      // after writing the delta, so if someone booked a part out while the count was being
      // filed, the toast says what the shelf holds rather than what was counted.
      pushNotification(intl.formatMessage(messages.toastStocktakeFiled, {
        quantity: formatQuantity(intl, result?.quantityOnHand ?? form.countedQuantity, 2),
      }));
      onClose?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.stocktakeSaveFailed)));
    }
  };

  const submitting = stocktakeMutation.isPending || stocktakeMutation.isLoading;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(messages.stocktakeTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {sparePart?.partNumber} · {sparePart?.name}
        </Typography>

        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.stocktakeBookBalance} />
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {formatQuantity(intl, balance, 2)} {sparePart?.unit}
          </Typography>
        </Stack>

        <Divider />

        <Box>
          <AxionControls.AxionTextBox
            name="countedQuantity"
            label={`${intl.formatMessage(messages.stocktakeCounted)}${sparePart?.unit ? ` (${sparePart.unit})` : ""}`}
            value={form.countedQuantity}
            onChange={handleTextChange}
          />
          <FieldError message={errors.countedQuantity} />
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
            <FormattedMessage {...messages.stocktakeHint} />
          </Typography>
        </Box>

        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.stocktakeDifference} />
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: delta === null ? "text.disabled" : getStockMovementQuantityColor(delta) }}
          >
            {delta === null ? "—" : `${delta > 0 ? "+" : ""}${formatQuantity(intl, delta, 2)}`}
          </Typography>
        </Stack>

        <AxionControls.AxionMultiLineText
          name="reason"
          label={intl.formatMessage(messages.movementReason)}
          value={form.reason}
          rows={2}
          onChange={handleTextChange}
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.save} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
